/**
 * `WidgetInstance` — the widget placement shared by every dashboard
 * composition (`dashboard/get`, `create`, `update`, `template/get`).
 *
 * The editor works in react-grid-layout's vocabulary (`layout: {i,x,y,w,h}`,
 * one grid), the contract in breakpoints (`layouts[]`, one entry per
 * `DESKTOP|TABLET|MOBILE`). This file is the only place that translation
 * lives, so the grid never learns about breakpoints and the wire never learns
 * about `i`.
 *
 * Two rules from the contract shape the mapping:
 *
 *   1. **DESKTOP is required to publish.** The editor only edits one grid, so
 *      that grid *is* DESKTOP; the other breakpoints are carried through
 *      untouched when the backend sent them.
 *   2. **New instances have no uuid.** They travel as `instanceUuid: null`
 *      plus a `clientRef`, and the write response hands back the
 *      `clientRef → instanceUuid` pairs so the editor can adopt the real ids
 *      without a reload.
 */

/** The breakpoint the editor's single grid maps to. */
export const EDITOR_BREAKPOINT = 'DESKTOP';

/** Section every instance lands in until the builder exposes sections. */
export const DEFAULT_SECTION_CODE = 'main';

/**
 * A placement the editor created in this session, not yet saved.
 *
 * `instanceId` is react-grid-layout's key and must stay stable across the
 * save, so the correlation token is derived from it rather than generated
 * fresh — otherwise the grid would remount every widget on every save.
 */
export const isClientInstanceId = (instanceId) =>
  typeof instanceId === 'string' && !/^[0-9a-f-]{36}$/i.test(instanceId);

/**
 * `WidgetInstance` → the widget the editor and the grid render.
 *
 * Everything the editor needs that the contract has no field for (the widget's
 * type, class and size options) rides in `visualizationOverrides`, which is
 * the contract's own free-form bag — and is where `toApiWidgetInstance` puts
 * it back.
 */
export const mapApiWidgetInstanceToUi = (raw = {}, index = 0) => {
  const overrides = raw.visualizationOverrides ?? {};
  const desktop = (raw.layouts ?? []).find((l) => l.breakpoint === EDITOR_BREAKPOINT) ?? null;
  const instanceId = raw.instanceUuid ?? raw.clientRef ?? `instance-${index}`;

  return {
    instanceId,
    // The catalog widget this placement pins, and the version it pins it at —
    // a dashboard does not follow a widget that changes under it.
    id: raw.widgetUuid ?? '',
    widgetUuid: raw.widgetUuid ?? '',
    widgetVersionNumber: raw.widgetVersionNumber ?? null,
    sectionCode: raw.sectionCode ?? DEFAULT_SECTION_CODE,
    name: raw.cardTitle ?? '',
    description: raw.cardDescription ?? '',
    type: overrides.type ?? '',
    category: overrides.category ?? '',
    libraryIcon: overrides.libraryIcon ?? null,
    widgetClass: overrides.widgetClass ?? 'CLASS_2_STANDARD',
    sizes: overrides.sizes ?? ['small', 'medium', 'large'],
    defaultSize: overrides.defaultSize ?? 'medium',
    actions: overrides.actions ?? [],
    acceptsGlobalFilters: raw.acceptsGlobalFilters ?? true,
    displayOrder: raw.displayOrder ?? index + 1,
    // Both locks come from the same toggle in the editor; the contract splits
    // them per axis, so a placement locked on either axis reads as locked.
    locked: Boolean(desktop?.sizeLock || desktop?.locationLock),
    layout: desktop
      ? {
        i: instanceId,
        x: desktop.posX ?? 0,
        y: desktop.posY ?? 0,
        w: desktop.width ?? 4,
        h: desktop.height ?? 2,
      }
      : null,
    // Kept verbatim so a save round-trips the breakpoints the editor cannot
    // edit instead of dropping TABLET and MOBILE on the floor.
    otherLayouts: (raw.layouts ?? []).filter((l) => l.breakpoint !== EDITOR_BREAKPOINT),
  };
};

/**
 * Editor widget → `WidgetInstance`.
 *
 * @param {object} widget    Editor state for one placement.
 * @param {number} index     Position in the grid, which becomes `displayOrder`.
 */
export const toApiWidgetInstance = (widget = {}, index = 0) => {
  const isNew = isClientInstanceId(widget.instanceId);
  const layout = widget.layout ?? null;

  return {
    instanceUuid: isNew ? null : widget.instanceId,
    ...(isNew ? { clientRef: widget.instanceId } : {}),
    widgetUuid: widget.widgetUuid ?? widget.id ?? '',
    widgetVersionNumber: widget.widgetVersionNumber ?? null,
    sectionCode: widget.sectionCode ?? DEFAULT_SECTION_CODE,
    cardTitle: widget.name ?? '',
    cardDescription: widget.description ?? null,
    visualizationOverrides: {
      type: widget.type ?? '',
      category: widget.category ?? '',
      libraryIcon: widget.libraryIcon ?? null,
      widgetClass: widget.widgetClass ?? 'CLASS_2_STANDARD',
      sizes: widget.sizes ?? [],
      defaultSize: widget.defaultSize ?? '',
      actions: widget.actions ?? [],
    },
    acceptsGlobalFilters: widget.acceptsGlobalFilters ?? true,
    displayOrder: index + 1,
    layouts: [
      // A placement that was never dragged has no layout yet; the grid assigns
      // one on first render, and publishing without it is what `DSW022`
      // rejects, so it is sent at the origin rather than omitted.
      {
        breakpoint: EDITOR_BREAKPOINT,
        posX: layout?.x ?? 0,
        posY: layout?.y ?? 0,
        width: layout?.w ?? 4,
        height: layout?.h ?? 2,
        sizeLock: Boolean(widget.locked),
        locationLock: Boolean(widget.locked),
        isHidden: false,
      },
      ...(widget.otherLayouts ?? []),
    ],
  };
};

/**
 * Adopt the `clientRef → instanceUuid` pairs a write returns, so the editor's
 * placements carry server ids from the first save on and the second save
 * updates them instead of creating duplicates.
 */
export const applyInstanceIds = (widgets = [], pairs = []) => {
  if (!pairs.length) return widgets;
  const byClientRef = new Map(pairs.map((p) => [p.clientRef, p.instanceUuid]));
  return widgets.map((widget) => {
    const assigned = byClientRef.get(widget.instanceId);
    if (!assigned) return widget;
    return {
      ...widget,
      instanceId: assigned,
      layout: widget.layout ? { ...widget.layout, i: assigned } : widget.layout,
    };
  });
};
